import { Truck, MapPin, CalendarDays, User, Package, Receipt, CalendarClock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";

const TYPE_CONFIG = {
  pickup: { label: "Ritiro", color: "#002FA7", icon: Truck },
  delivery: { label: "Consegna", color: "#059669", icon: MapPin },
  inventory_pickup: { label: "Inventario — Ritiro", color: "#7C3AED", icon: Package },
  inventory_delivery: { label: "Inventario — Consegna", color: "#D97706", icon: Package },
  release_due: { label: "Riconsegna prevista", color: "#EAB308", icon: Receipt },
};

function getDocLink(event) {
  if (event.doc_type === "proforma") {
    return { path: `/proformas/${event.doc_id}`, label: "Apri Proforma Riconsegna" };
  }
  if (event.doc_type === "inventory") {
    return { path: `/inventories/${event.doc_id}`, label: "Apri Inventario" };
  }
  return { path: `/quotes/${event.doc_id || event.quote_id}`, label: "Apri Preventivo" };
}

function nextDayIso(iso) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function formatItalianDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

export function EventDetailModal({ event, onClose, canEdit, onRescheduleRelease }) {
  const navigate = useNavigate();
  const [linkedInventories, setLinkedInventories] = useState([]);

  useEffect(() => {
    if (!event?.quote_id) { setLinkedInventories([]); return; }
    let cancelled = false;
    api.get(`/inventories?quote_id=${event.quote_id}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const items = Array.isArray(data) ? data : (data.items || []);
        setLinkedInventories(items);
      })
      .catch(() => { if (!cancelled) setLinkedInventories([]); });
    return () => { cancelled = true; };
  }, [event?.quote_id]);

  if (!event) return null;

  const config = TYPE_CONFIG[event.type] || TYPE_CONFIG.pickup;
  const isProforma = event.doc_type === "proforma";
  const isReleaseDue = event.type === "release_due";
  const isInventoryEvent = event.doc_type === "inventory";
  const wantsRelease = isReleaseDue;
  const wantsPickup = !isReleaseDue && !isInventoryEvent;
  const releaseInv = linkedInventories.filter(i => i.inventory_type === "release").slice(-1)[0];
  const pickupInv = linkedInventories.filter(i => i.inventory_type !== "release").slice(-1)[0];
  const linkedInv = wantsRelease ? releaseInv : (wantsPickup ? pickupInv : null);
  const linkedKind = wantsRelease ? "release" : "pickup";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="event-detail-overlay"
    >
      <div
        className="bg-white rounded-sm shadow-xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="event-detail-modal"
      >
        <ModalHeader config={config} number={event.number} />
        <div className="px-5 py-4 space-y-3">
          <DetailsList event={event} />
          {linkedInv && (
            <LinkedInventoryChip
              inv={linkedInv}
              kind={linkedKind}
              onOpen={() => { onClose(); navigate(`/inventories/${linkedInv.id}`); }}
            />
          )}
          {isReleaseDue && canEdit && (
            <RescheduleReleaseSection
              event={event}
              onRescheduleRelease={onRescheduleRelease}
              onClose={onClose}
            />
          )}
        </div>
        <ModalActions
          event={event}
          isProforma={isProforma}
          isReleaseDue={isReleaseDue}
          hasReleaseInventory={!!releaseInv}
          navigate={navigate}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

function ModalHeader({ config, number }) {
  const Icon = config.icon;
  return (
    <div className="px-5 py-4" style={{ backgroundColor: config.color }}>
      <div className="flex items-center gap-2 text-white">
        <Icon className="w-5 h-5" />
        <h3 className="font-semibold text-lg">{config.label}</h3>
      </div>
      <p className="text-white/80 text-sm mt-1">{number}</p>
    </div>
  );
}

function DetailsList({ event }) {
  const address = [event.address, event.city].filter(Boolean).join(", ");
  return (
    <>
      <DetailRow icon={CalendarDays} label="Data" value={formatItalianDate(event.date)} />
      {event.customer_name && <DetailRow icon={User} label="Cliente" value={event.customer_name} />}
      {(event.address || event.city) && <DetailRow icon={MapPin} label="Indirizzo" value={address} />}
      {event.assigned_to_name && <DetailRow icon={Truck} label="Operatore" value={event.assigned_to_name} />}
      {event.subject && <DetailRow icon={CalendarDays} label="Oggetto" value={event.subject} />}
    </>
  );
}

function RescheduleReleaseSection({ event, onRescheduleRelease, onClose }) {
  const [newReleaseDate, setNewReleaseDate] = useState("");
  const minNewDate = nextDayIso(event.date);

  const handleReschedule = () => {
    if (!newReleaseDate) {
      toast.error("Seleziona una data");
      return;
    }
    if (newReleaseDate === event.date) {
      toast.info("La nuova data coincide con quella attuale");
      return;
    }
    if (newReleaseDate < event.date) {
      toast.error("La nuova data deve essere successiva a quella attuale");
      return;
    }
    onRescheduleRelease?.({
      proformaId: event.doc_id,
      oldDate: event.date,
      newDate: newReleaseDate,
    });
    onClose();
  };

  return (
    <div className="pt-3 mt-2 border-t border-zinc-100" data-testid="reschedule-release-section">
      <div className="flex items-start gap-3">
        <CalendarClock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1 space-y-2">
          <p className="text-xs text-zinc-400">Sposta riconsegna</p>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={newReleaseDate}
              min={minNewDate}
              onChange={(e) => setNewReleaseDate(e.target.value)}
              className="flex-1 text-sm px-2 py-1.5 border border-zinc-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="reschedule-release-date-input"
            />
            <button
              onClick={handleReschedule}
              disabled={!newReleaseDate}
              className="px-3 py-1.5 text-xs font-medium rounded-sm bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reschedule-release-apply-btn"
            >
              Sposta
            </button>
          </div>
          <p className="text-[11px] text-zinc-400 italic leading-snug">
            Se la nuova data supera di almeno 1 mese calendario quella attuale, verrà creata una proforma di estensione in BOZZA.
          </p>
        </div>
      </div>
    </div>
  );
}

function LinkedInventoryChip({ inv, kind, onOpen }) {
  const isRelease = kind === "release";
  const label = isRelease ? "Inventario Riconsegna pronto" : "Inventario di Ritiro pronto";
  const styles = isRelease
    ? "border-amber-300 bg-amber-50/60 hover:bg-amber-100/70"
    : "border-[#002FA7]/30 bg-blue-50/60 hover:bg-blue-100/70";
  const iconColor = isRelease ? "text-amber-700" : "text-[#002FA7]";
  return (
    <button
      onClick={onOpen}
      data-testid={isRelease ? "event-linked-release-inventory-chip" : "event-linked-pickup-inventory-chip"}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm border transition ${styles}`}
    >
      <Package className={`w-4 h-4 shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0 text-left">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
          <span className="font-mono text-xs">{inv.number || "BOZZA"}</span>
          <StatusBadge status={inv.status} />
        </div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
    </button>
  );
}

function ModalActions({ event, isProforma, isReleaseDue, hasReleaseInventory, navigate, onClose }) {
  const [creating, setCreating] = useState(false);
  const docLink = getDocLink(event);

  const handleCreateReleaseInventory = async () => {
    if (!event.quote_id) {
      toast.error("Preventivo non disponibile per questo evento");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post(`/inventories/release-from-quote/${event.quote_id}`);
      toast.success("Inventario di Riconsegna creato");
      onClose();
      navigate(`/inventories/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="px-5 py-3 border-t border-zinc-100 flex flex-wrap justify-between items-center gap-2">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => navigate(docLink.path)}
          className="btn-primary text-xs"
          data-testid="event-detail-open-doc"
        >
          {docLink.label}
        </button>
        {isProforma && event.quote_id && (
          <button
            onClick={() => navigate(`/quotes/${event.quote_id}`)}
            className="btn-secondary text-xs"
            data-testid="event-detail-open-quote"
          >
            Apri Preventivo
          </button>
        )}
        {isReleaseDue && event.quote_id && !hasReleaseInventory && (
          <button
            onClick={handleCreateReleaseInventory}
            disabled={creating}
            className="btn-secondary text-xs disabled:opacity-50"
            data-testid="event-detail-create-release-inventory"
          >
            {creating ? "Creazione..." : "Crea Inventario Riconsegna"}
          </button>
        )}
      </div>
      <button onClick={onClose} className="btn-secondary text-xs" data-testid="event-detail-close">
        Chiudi
      </button>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-zinc-400">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}
